/**
 * Session Monitor Component
 * Monitors JWT token expiration and prompts user to extend session
 *
 * CRITICAL: This component works alongside:
 * - InactivityMonitor (60 min inactivity timeout)
 * - ActivityBasedTokenRefresh (proactive token refresh for active users)
 *
 * This is the LAST LINE OF DEFENSE - it catches cases where:
 * 1. User was inactive and ActivityBasedTokenRefresh stopped refreshing
 * 2. Token refresh failed for some reason
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { getTokenInfo, clearAuthData, isAuthenticated } from '../utils/tokenUtils';
import { authRedirect } from '../utils/authRedirect';

const SessionMonitor = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);

  // Use refs to avoid dependency issues
  const intervalRef = useRef(null);
  const countdownRef = useRef(null); // For live countdown in warning modal
  const warningShownRef = useRef(false);

  // Configuration constants
  const WARNING_THRESHOLD = 5 * 60; // 5 minutes (300 seconds) - show warning
  const CRITICAL_THRESHOLD = 30;    // 30 seconds - auto-logout (was 60, reduced for better UX)
  const CHECK_INTERVAL = 30 * 1000; // 30 seconds - how often to check token

  // Handle logout - memoized to avoid recreating
  const handleLogout = useCallback((reason = 'session_expired') => {
    console.log('🚪 SessionMonitor: Logging out -', reason);

    // Clear all intervals
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }

    // Clear auth data
    clearAuthData();

    // Redirect to login with reason (centralized guard prevents storms)
    authRedirect('/login', reason);
  }, []);

  // Check token status - memoized
  const checkToken = useCallback(() => {
    if (!isAuthenticated()) {
      return;
    }

    const tokenInfo = getTokenInfo();

    if (!tokenInfo.exists || tokenInfo.expired) {
      console.log('🚪 SessionMonitor: JWT token expired - logging out');
      handleLogout('token_expired');
      return;
    }

    const timeUntilExpiry = tokenInfo.timeUntilExpiry;
    setTimeLeft(timeUntilExpiry);

    // Show warning once when threshold reached
    if (timeUntilExpiry <= WARNING_THRESHOLD &&
        timeUntilExpiry > CRITICAL_THRESHOLD &&
        !warningShownRef.current) {
      setShowWarning(true);
      warningShownRef.current = true;
      console.log(`⚠️ SessionMonitor: Token expiring in ${Math.floor(timeUntilExpiry / 60)} minutes`);

      // Start live countdown when warning shows
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
      countdownRef.current = setInterval(() => {
        const info = getTokenInfo();
        if (info.exists && !info.expired) {
          setTimeLeft(info.timeUntilExpiry);

          // Auto-logout at critical threshold
          if (info.timeUntilExpiry <= CRITICAL_THRESHOLD) {
            handleLogout('auto_expired');
          }
        }
      }, 1000); // Update every second for live countdown
    }

    // Auto-logout at critical threshold
    if (timeUntilExpiry <= CRITICAL_THRESHOLD) {
      handleLogout('auto_expired');
    }
  }, [handleLogout]);

  // Extend session - memoized
  const handleExtendSession = useCallback(async () => {
    try {
      // Import dynamically to avoid circular dependencies
      const { refreshAuthToken } = await import('../utils/tokenUtils');

      const success = await refreshAuthToken();

      // Clear countdown interval
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }

      if (success) {
        console.log('✅ SessionMonitor: Session extended successfully');
        setShowWarning(false);
        warningShownRef.current = false;

        // Reset the check to update the new token expiry
        checkToken();
      } else {
        console.warn('⚠️ SessionMonitor: Token refresh failed');
        // Keep modal open and show error - don't just close it
        // User needs to know refresh failed
        setShowWarning(false);
        warningShownRef.current = false; // Allow warning to show again if threshold reached
      }
    } catch (error) {
      console.error('❌ SessionMonitor: Session extension failed:', error.message);
      setShowWarning(false);
      warningShownRef.current = false;
    }
  }, [checkToken]);

  // Setup monitoring
  useEffect(() => {
    if (!isAuthenticated()) {
      return;
    }

    // Initial check
    checkToken();

    // Start periodic check interval
    intervalRef.current = setInterval(checkToken, CHECK_INTERVAL);

    /**
     * CRITICAL: Check token when tab becomes visible
     * User might have been on another tab while token expired
     */
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ SessionMonitor: Tab visible - checking token');
        checkToken();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkToken]); // Include checkToken in deps

  // Don't render if not authenticated
  if (!isAuthenticated()) {
    return <div data-testid="session-monitor" style={{ display: 'none' }} />;
  }

  // Session warning modal
  if (showWarning && timeLeft) {
    const minutesLeft = Math.floor(timeLeft / 60);
    const secondsLeft = timeLeft % 60;

    return (
      <div 
        data-testid="session-monitor"
        style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}>
        <div className="bg-white" style={{
          padding: '30px',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
          maxWidth: '400px',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '24px', marginBottom: '15px' }}>⚠️</div>
          <h3 style={{ margin: '0 0 15px 0', color: '#d97706' }}>
            Session Expiring Soon
          </h3>
          <p style={{ margin: '0 0 20px 0', color: '#374151' }}>
            Your session will expire in{' '}
            <strong style={{ color: '#dc2626' }}>
              {minutesLeft}:{secondsLeft.toString().padStart(2, '0')}
            </strong>
          </p>
          <p style={{ margin: '0 0 25px 0', fontSize: '14px', color: '#6b7280' }}>
            Click "Continue" to keep working, or you will be automatically logged out.
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
            <button
              onClick={handleExtendSession}
              style={{
                backgroundColor: '#059669',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Continue Working
            </button>
            <button
              onClick={() => handleLogout('user_logout')}
              style={{
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Logout Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <div data-testid="session-monitor" style={{ display: 'none' }} />;
};

export default SessionMonitor;